import React, { useState, useCallback, useEffect } from 'react';
import { formatDateTimeLocale } from '../../utils/dateFormat';
import { useParams, useNavigate } from 'react-router-dom';
import { useTreatXAdminAuth } from '../../context/TreatXAdminAuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import {
  ArrowLeft, Shield, CheckCircle, XCircle, Clock,
  Loader2, Search, Eye, FileText, AlertTriangle, Image, RefreshCw
} from 'lucide-react';
import api from '../../utils/api';

function getAuthHeaders() {
  return { 'Content-Type': 'application/json' };
}

const STATUS_MAP = {
  approved: { label: 'Approved', cls: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  rejected: { label: 'Rejected', cls: 'bg-red-100 text-red-700', icon: XCircle },
  unreviewed: { label: 'Unreviewed', cls: 'bg-amber-100 text-amber-700', icon: Clock },
};

function QueueItem({ item, onSelect, onAction, acting }) {
  const s = STATUS_MAP[item.moderation_status] || STATUS_MAP.unreviewed;
  const SIcon = s.icon;
  return (
    <div className="flex items-center gap-3 py-2.5 px-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50/60 transition-colors" data-testid={`queue-item-${item.clinic_id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-[#122033] truncate">{item.clinic_name}</p>
          {item.has_before_after && (
            <Badge variant="outline" className="text-[9px] border-amber-200 text-amber-600 gap-0.5 shrink-0">
              <Image className="h-2.5 w-2.5" /> B/A
            </Badge>
          )}
          {item.compliance_mode === 'TR_STRICT' && (
            <Badge variant="outline" className="text-[9px] border-red-200 text-red-500 shrink-0">TR</Badge>
          )}
        </div>
        {item.tagline && <p className="text-[11px] text-slate-400 truncate mt-0.5">{item.tagline}</p>}
        {item.moderation_notes && (
          <p className="text-[10px] text-slate-400 italic mt-0.5 truncate">{item.moderation_notes}</p>
        )}
        <p className="text-[10px] text-slate-300 mt-0.5">
          {item.updated_at_utc ? formatDateTimeLocale(item.updated_at_utc) : 'No date'}
        </p>
      </div>
      <Badge className={`text-[10px] px-1.5 py-0.5 shrink-0 ${s.cls}`} data-testid={`queue-status-${item.clinic_id}`}>
        <SIcon className="h-2.5 w-2.5 mr-0.5" />{s.label}
      </Badge>
      <div className="flex gap-1 shrink-0">
        {item.moderation_status !== 'approved' && (
          <Button size="sm" className="h-7 text-[10px] bg-emerald-600 hover:bg-emerald-700 text-white px-2" disabled={acting}
            onClick={(e) => { e.stopPropagation(); onAction(item.clinic_id, 'approved'); }}
            data-testid={`queue-approve-${item.clinic_id}`}>
            <CheckCircle className="h-3 w-3" />
          </Button>
        )}
        {item.moderation_status !== 'rejected' && (
          <Button size="sm" variant="destructive" className="h-7 text-[10px] px-2" disabled={acting}
            onClick={(e) => { e.stopPropagation(); onAction(item.clinic_id, 'rejected'); }}
            data-testid={`queue-reject-${item.clinic_id}`}>
            <XCircle className="h-3 w-3" />
          </Button>
        )}
        <Button size="sm" variant="outline" className="h-7 text-[10px] px-2" onClick={() => onSelect(item.clinic_id)}
          data-testid={`queue-review-${item.clinic_id}`}>
          <Eye className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}

export default function AdminProfileReview() {
  const { clinicId: paramClinicId } = useParams();
  const { admin } = useTreatXAdminAuth();
  const navigate = useNavigate();

  // Queue state
  const [queue, setQueue] = useState([]);
  const [queueLoading, setQueueLoading] = useState(false);
  const [queueFilter, setQueueFilter] = useState('unreviewed');
  const [queueActing, setQueueActing] = useState(false);

  // Single clinic review state
  const [clinicId, setClinicId] = useState(paramClinicId || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState('');

  const loadQueue = useCallback(async (filter) => {
    setQueueLoading(true);
    try {
      const statusParam = filter === 'all' ? '' : `?status=${filter}`;
      const res = await api.get(`/admin/clinics/moderation-queue${statusParam}`);
      const d = res.data;
      setQueue(d.items || []);
    } catch (e) { toast.error(`Queue load failed: ${e.message}`); }
    finally { setQueueLoading(false); }
  }, []);

  useEffect(() => { if (admin) loadQueue(queueFilter); }, [admin, queueFilter, loadQueue]);

  const handleQueueAction = async (cid, status) => {
    setQueueActing(true);
    try {
      const res = await api.post(`/admin/clinics/${cid}/profile-review`);
      toast.success(`${status === 'approved' ? 'Approved' : 'Rejected'}`);
      loadQueue(queueFilter);
      // If this clinic is also loaded in detail, refresh it
      if (data?.clinic_id === cid) loadReview(cid);
    } catch (e) { toast.error(e.message); }
    finally { setQueueActing(false); }
  };

  const loadReview = useCallback(async (id) => {
    const cid = id || clinicId;
    if (!cid.trim()) { toast.error('Enter a clinic ID'); return; }
    setLoading(true);
    try {
      const res = await api.get(`/admin/clinics/${cid.trim()}/profile-review`);
      if (res.status === 404) { toast.error('Clinic not found'); return; }
      const d = res.data;
      setData(d);
      setClinicId(cid.trim());
      setNotes(d.moderation?.notes || '');
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [clinicId]);

  useEffect(() => { if (paramClinicId) loadReview(paramClinicId); }, [paramClinicId]);

  const handleDecision = async (status) => {
    const cid = data?.clinic_id || clinicId;
    if (!cid) return;
    setSaving(true);
    try {
      const res = await api.post(`/admin/clinics/${cid}/profile-review`);
      if (false) { /* axios throws on error */ const d = res.data; throw new Error(d.detail || `HTTP ${res.status}`); }
      const d = res.data;
      setData(prev => ({ ...prev, moderation: d.moderation }));
      toast.success(`Profile ${status}`);
      loadQueue(queueFilter);
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  if (!admin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <Card className="w-96"><CardContent className="p-6 text-center">
          <Shield className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500 mb-3">Admin login required</p>
          <Button onClick={() => navigate('/login')} data-testid="login-redirect">Sign In</Button>
        </CardContent></Card>
      </div>
    );
  }

  const mod = data?.moderation || {};
  const statusInfo = STATUS_MAP[mod.status] || STATUS_MAP.unreviewed;
  const StatusIcon = statusInfo.icon;
  const pp = data?.profile_public || {};

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-profile-review">
      <header className="bg-[#122033] text-white border-b border-white/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="text-white/70 hover:text-white hover:bg-white/10" onClick={() => navigate('/treatx-admin/dashboard')} data-testid="back-to-admin">
              <ArrowLeft className="h-4 w-4 mr-1" /> Admin
            </Button>
            <div className="h-5 w-px bg-white/20" />
            <h1 className="text-lg font-semibold tracking-tight font-[Manrope] flex items-center gap-2">
              <Eye className="h-4.5 w-4.5" /> Moderation Queue
            </h1>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
          {/* LEFT: Queue */}
          <div className="lg:col-span-2 space-y-3">
            <Card className="border border-slate-200/60 shadow-sm" data-testid="moderation-queue-panel">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-semibold text-[#122033] font-[Manrope]">Review Queue</CardTitle>
                  <Button variant="ghost" size="sm" className="h-7 text-xs text-slate-400 gap-1" onClick={() => loadQueue(queueFilter)} disabled={queueLoading} data-testid="queue-refresh">
                    <RefreshCw className={`h-3 w-3 ${queueLoading ? 'animate-spin' : ''}`} /> Refresh
                  </Button>
                </div>
                <div className="flex gap-1 pt-1">
                  {['unreviewed', 'approved', 'rejected', 'all'].map(f => (
                    <Button key={f} variant={queueFilter === f ? 'default' : 'outline'} size="sm"
                      className={`h-6 text-[10px] px-2 ${queueFilter === f ? 'bg-[#122033] text-white' : ''}`}
                      onClick={() => setQueueFilter(f)}
                      data-testid={`queue-filter-${f}`}>
                      {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
                    </Button>
                  ))}
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {queueLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-5 w-5 text-slate-300 animate-spin" />
                  </div>
                ) : queue.length === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle className="h-8 w-8 text-emerald-200 mx-auto mb-2" />
                    <p className="text-xs text-slate-400">No items in queue</p>
                  </div>
                ) : (
                  <div className="max-h-[500px] overflow-y-auto" data-testid="queue-list">
                    {queue.map(item => (
                      <QueueItem key={item.clinic_id} item={item} onSelect={(id) => { setClinicId(id); loadReview(id); }} onAction={handleQueueAction} acting={queueActing} />
                    ))}
                  </div>
                )}
                <div className="px-3 py-1.5 border-t border-slate-100 text-[10px] text-slate-300" data-testid="queue-count">
                  {queue.length} item{queue.length !== 1 ? 's' : ''}
                </div>
              </CardContent>
            </Card>

            {/* Quick lookup */}
            <Card className="border border-slate-200/60 shadow-sm">
              <CardContent className="p-3">
                <div className="flex gap-2" data-testid="clinic-search">
                  <Input placeholder="Clinic ID" value={clinicId} onChange={(e) => setClinicId(e.target.value)} className="text-xs h-8" data-testid="review-clinic-id-input" />
                  <Button onClick={() => loadReview()} disabled={loading} size="sm" className="gap-1 h-8 text-xs" data-testid="load-review-btn">
                    {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Search className="h-3 w-3" />} Load
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* RIGHT: Detail Panel */}
          <div className="lg:col-span-3 space-y-4">
            {!data ? (
              <Card className="border border-dashed border-slate-200 shadow-none">
                <CardContent className="p-8 text-center">
                  <Eye className="h-8 w-8 text-slate-200 mx-auto mb-2" />
                  <p className="text-xs text-slate-400">Select a clinic from the queue or search by ID</p>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Clinic Header */}
                <Card className="border border-slate-200/60 shadow-sm" data-testid="detail-header">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-base font-semibold text-[#122033] font-[Manrope]">{data.clinic_id}</h2>
                        <p className="text-xs text-slate-400 mt-0.5">{pp.about?.tagline ? (typeof pp.about.tagline === 'object' ? pp.about.tagline.en || Object.values(pp.about.tagline)[0] : pp.about.tagline) : 'No tagline'}</p>
                      </div>
                      <Badge className={`text-xs px-2 py-0.5 ${statusInfo.cls}`} data-testid="review-status-badge">
                        <StatusIcon className="h-3 w-3 mr-0.5" />{statusInfo.label}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>

                {/* Moderation Panel */}
                <Card className="border border-slate-200/60 shadow-sm" data-testid="moderation-panel">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold text-[#122033] font-[Manrope]">Moderation Decision</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {mod.reviewer_id && <p className="text-xs text-slate-400">Reviewed by {mod.reviewer_id} on {mod.reviewed_at_utc ? formatDateTimeLocale(mod.reviewed_at_utc) : 'N/A'}</p>}

                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="flex items-center gap-1.5">
                        <Label className="text-xs text-slate-500">Compliance:</Label>
                        <Badge variant="outline" className={`text-[10px] ${pp.visibility_policy?.compliance_mode === 'TR_STRICT' ? 'border-amber-200 text-amber-700' : 'border-slate-200 text-slate-500'}`} data-testid="review-compliance">
                          {pp.visibility_policy?.compliance_mode || 'GLOBAL'}
                        </Badge>
                      </div>
                      {pp.visibility_policy?.show_before_after && (
                        <Badge variant="outline" className="text-[10px] border-amber-200 text-amber-600 gap-0.5" data-testid="review-ba-flag">
                          <Image className="h-2.5 w-2.5" /> Before/After enabled
                        </Badge>
                      )}
                    </div>

                    {/* Content Summary */}
                    <div className="bg-slate-50 rounded-md p-2.5 space-y-1">
                      <p className="text-[10px] text-slate-400 uppercase tracking-wider">Content Summary</p>
                      {pp.about?.long_description && <p className="text-[11px] text-slate-500 truncate">Description: {typeof pp.about.long_description === 'object' ? pp.about.long_description.en || '(multilingual)' : pp.about.long_description.substring(0, 100) + '...'}</p>}
                      {pp.social_links && Object.keys(pp.social_links).length > 0 && <p className="text-[11px] text-slate-500">Social links: {Object.keys(pp.social_links).filter(k => pp.social_links[k]).join(', ') || 'None'}</p>}
                      {pp.treatment_showcase && (
                        <p className="text-[11px] text-slate-500">Treatments: {Object.keys(pp.treatment_showcase).join(', ')}</p>
                      )}
                    </div>

                    {/* Before/After warning */}
                    {pp.visibility_policy?.show_before_after && pp.treatment_showcase && (
                      <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="ba-review-warning">
                        <p className="text-[11px] text-amber-800 flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3 shrink-0" />
                          This clinic has Before/After content enabled. Review consent and compliance before approving.
                        </p>
                      </div>
                    )}

                    <div>
                      <Label className="text-xs text-slate-500 uppercase tracking-wider mb-1 block">Review Notes</Label>
                      <textarea className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm resize-y min-h-[60px] focus:outline-none focus:ring-1 focus:ring-slate-300"
                        value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Add review notes..." data-testid="review-notes" />
                    </div>

                    <div className="flex gap-2 pt-1">
                      <Button onClick={() => handleDecision('approved')} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5 text-xs" data-testid="approve-btn">
                        {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle className="h-3.5 w-3.5" />} Approve
                      </Button>
                      <Button onClick={() => handleDecision('rejected')} disabled={saving} variant="destructive" className="gap-1.5 text-xs" data-testid="reject-btn">
                        {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <XCircle className="h-3.5 w-3.5" />} Reject
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Raw Data */}
                <Card className="border border-slate-200/60 shadow-sm" data-testid="raw-data-panel">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold text-[#122033] font-[Manrope] flex items-center gap-2">
                      <FileText className="h-4 w-4 text-slate-400" /> Profile Data
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <pre className="bg-slate-50 rounded-md p-3 text-xs text-slate-600 overflow-x-auto max-h-[300px] overflow-y-auto" data-testid="raw-profile-data">
                      {JSON.stringify(pp, null, 2)}
                    </pre>
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
